/**
 * messageMatch — lógica PURA de casamento e navegação da busca dentro da
 * conversa (in-conversation search). Sem DOM, sem React: unit-testável.
 *
 * `computeMatches(messages, query)` devolve, na ORDEM em que as mensagens
 * aparecem na lista (cronológica, como renderizada), os ids das mensagens cujo
 * corpo casa com o termo. `stepMatch(matches, current, dir)` move o índice
 * "atual" entre as ocorrências, com wrap-around (circular).
 *
 * A normalização é IDÊNTICA à do messageStore/messageSearch (NFD + remove
 * diacríticos + lowercase) para que o contador/navegação in-view seja
 * consistente com o que a facade `searchMessages` casaria.
 */

/** NFD + remove diacríticos + lowercase. "transação" -> "transacao", "Olá" -> "ola". */
export function normalizeMatch(s: string): string {
  return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
}

export interface MatchableMessage {
  id: number
  body: string | null
}

/**
 * Ids das mensagens (na ordem recebida) cujo corpo contém o termo normalizado.
 * Query vazia/só-espaços → sem matches.
 */
export function computeMatches(messages: MatchableMessage[], query: string): number[] {
  const q = normalizeMatch(query.trim())
  if (!q) return []
  const out: number[] = []
  for (const m of messages) {
    if (!m.body) continue
    if (normalizeMatch(m.body).includes(q)) out.push(m.id)
  }
  return out
}

/**
 * Move o índice atual entre as ocorrências, com wrap-around.
 * - `dir` = +1 (próxima) ou -1 (anterior).
 * - Sem matches → -1.
 * - `current` fora de faixa (ex.: -1 inicial) → começa do início/fim conforme dir.
 */
export function stepMatch(matches: number[], current: number, dir: 1 | -1): number {
  const n = matches.length
  if (n === 0) return -1
  if (current < 0 || current >= n) {
    return dir === 1 ? 0 : n - 1
  }
  return (current + dir + n) % n
}

/**
 * Reposiciona o índice "atual" após um RECÁLCULO dos matches (ex.: chegou uma
 * mensagem ao vivo enquanto a barra de busca está aberta) preservando a POSIÇÃO
 * do usuário sempre que possível — em vez de saltar arbitrariamente para o
 * primeiro/último match.
 *
 * Regras:
 * - novo conjunto vazio → -1.
 * - se o id do match atual (oldMatches[oldIndex]) ainda existe em newMatches,
 *   retorna o novo índice desse mesmo id (preserva-por-id).
 * - senão, faz clamp do índice antigo dentro da nova faixa (0..n-1).
 *
 * Só deve ser usado quando a QUERY não mudou. Ao trocar a query, o chamador
 * reposiciona explicitamente (tipicamente no primeiro match).
 */
export function remapIndex(
  oldMatches: number[],
  oldIndex: number,
  newMatches: number[],
): number {
  const n = newMatches.length
  if (n === 0) return -1
  const currentId =
    oldIndex >= 0 && oldIndex < oldMatches.length ? oldMatches[oldIndex] : undefined
  if (currentId !== undefined) {
    const found = newMatches.indexOf(currentId)
    if (found !== -1) return found
  }
  // Preserva-por-id impossível → clamp do índice antigo na nova faixa.
  if (oldIndex < 0) return 0
  if (oldIndex >= n) return n - 1
  return oldIndex
}
